import { CancelledError, useMutation, useQuery } from '@tanstack/react-query';

import { ROUTES } from '@cloud-ui/constants';
import { useAuth } from '@cloud-ui/contexts/AuthContext';
import { useToast } from '@cloud-ui/contexts/ToastContext';
import { ApiError } from '@cloud-ui/utils/api';
import {
  acceptInvite,
  getUserOrganizations,
  getUserTeamAbilities,
  previewInvite,
  switchOrganization,
} from '@cloud-ui/utils/api/users';
import {
  announceOrganizationChange,
  ORGANIZATION_SESSION_MUTATION_KEY,
  trackAuthSession,
} from '@cloud-ui/utils/authSessionStorage';
import type { AcceptInviteRequest } from '@shared/dto/users';

import { useNavigate } from 'react-router-dom';

export const useUserTeamAbilities = (teamId?: string) => {
  const { user, organization } = useAuth();
  return useQuery({
    // Keep the team prefix for role invalidation while isolating each identity's rules.
    queryKey: ['users', 'me', 'teams', teamId, 'abilities', user?.id, organization?.id],
    queryFn: () => (teamId ? getUserTeamAbilities(teamId) : null),
    enabled: !!teamId && !!user,
    staleTime: 30000, // 30 seconds
  });
};

export const useUserOrganizations = (options?: { handle401Locally?: boolean }) => {
  const { user } = useAuth();
  return useQuery({
    // Invite recovery must not share a request that uses the global 401/logout policy.
    queryKey: options?.handle401Locally
      ? ['users', 'me', 'organizations', 'local401']
      : ['users', 'me', 'organizations'],
    queryFn: async () => {
      if (!options?.handle401Locally) {
        return getUserOrganizations();
      }
      const session = trackAuthSession();
      try {
        const organizations = await getUserOrganizations({ handle401Locally: true });
        if (!session.isCurrent()) {
          throw new CancelledError({ revert: true });
        }
        return organizations;
      } catch (error) {
        if (!session.isCurrent()) {
          throw new CancelledError({ revert: true });
        }
        throw error;
      } finally {
        session.dispose();
      }
    },
    enabled: !!user,
    // A cancelled old-account read must not retry under the replacement session.
    retry: options?.handle401Locally ? false : undefined,
  });
};

/**
 * Asks the server which organization an invite code admits to. The invite page decides what to
 * show from the answer, including a refusal, so it is asked once: not retried, and not asked
 * again behind a card the user is reading. Accepting checks the invite again on the server.
 */
export const useInvitePreview = (inviteCode: string, options?: { enabled?: boolean }) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['users', 'me', 'invites', 'preview', inviteCode],
    queryFn: async () => {
      const session = trackAuthSession();
      try {
        const response = await previewInvite(inviteCode);
        if (!session.isCurrent()) {
          throw new CancelledError({ revert: true });
        }
        return response;
      } catch (error) {
        if (!session.isCurrent()) {
          // Discard both successful and failed answers belonging to an ended account.
          throw new CancelledError({ revert: true });
        }
        throw error;
      } finally {
        session.dispose();
      }
    },
    enabled: !!user && (options?.enabled ?? true),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // The key holds the code, so the entry is dropped as soon as the page stops using it.
    gcTime: 0,
  });
};

/** Accepts an invite and notifies peers before the session write settles, even after unmount. */
export const useAcceptInvite = (onSettled?: (error: unknown | null) => void) => {
  return useMutation({
    mutationKey: ORGANIZATION_SESSION_MUTATION_KEY,
    mutationFn: async (request: AcceptInviteRequest) => {
      const session = trackAuthSession();
      try {
        const accepted = await acceptInvite(request);
        if (session.isCurrent()) {
          // Notify before this write settles, even if its card has since unmounted.
          announceOrganizationChange();
          onSettled?.(null);
          return accepted;
        }
      } catch (error) {
        if (session.isCurrent()) {
          onSettled?.(error);
          throw error;
        }
      } finally {
        session.dispose();
      }
      // A response for an ended session must not act on the account now using this browser.
      return undefined;
    },
  });
};

export const useSwitchOrganization = (options?: { skipNavigation?: boolean }) => {
  const { login, reloadSession } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  return useMutation({
    mutationKey: ORGANIZATION_SESSION_MUTATION_KEY,
    mutationFn: async (organizationId: string) => {
      const session = trackAuthSession();
      try {
        const selected = await switchOrganization(organizationId);
        if (!session.isCurrent()) {
          return undefined;
        }
        // Notify while this write is still pending, including when its component unmounted.
        announceOrganizationChange();
        await login();
        if (session.isCurrent() && !options?.skipNavigation) {
          navigate('/');
        }
        return session.isCurrent() ? selected : undefined;
      } catch (error) {
        if (!session.isCurrent()) {
          return undefined;
        }
        if (error instanceof ApiError && error.status === 401) {
          // Reload to revalidate the browser's current cookie; a late answer must never
          // issue a logout request that could revoke a replacement account.
          const returnPath = window.location.pathname + window.location.search;
          reloadSession(
            `${ROUTES.login}?reason=unauthorized&redirect=${encodeURIComponent(returnPath)}`,
          );
          throw error;
        }
        console.error(error);
        const errorMessage =
          (error as any)?.response?.data?.error?.message ??
          (error instanceof Error && error.message
            ? error.message
            : 'Unexpected error. Please try again.');
        showToast(`Failed to switch organization: ${errorMessage}`, 'error');
        throw error;
      } finally {
        session.dispose();
      }
    },
  });
};
